// Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
//
// This code and associated materials are protected by copyright laws and
// international treaties. Unauthorized use, reproduction, distribution, or
// modification of any content from Knowledge for Decisions (K4D) is strictly
// prohibited. For inquiries regarding the use or licensing of K4D's
// copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
// for Decisions (K4D)" and the slogan "Using data to make precise decisions"
// are registered trademarks of Knowledge for Decisions (K4D).

/**
 * The agency tab: one card that sells seats, and one that sells a conversation.
 *
 * An agency does not pick a tier the way a solo broker does — it picks a team,
 * and the team can mix tiers. Three tier cards side by side asked the buyer to
 * choose one *for everyone*, which is not the product: the seats are chosen at
 * checkout, one counter per tier. So the tiers become rows inside a single
 * "Imobiliária" card, and the card as a whole is what the buyer picks.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text, UnstyledButton } from '@/shared/ui';
import { getSpotsStatus, isPlanSelectable } from '../../hooks/use-plans';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import {
  agencyRowFacts, enabledFeatures, featureMatrix, formatStorage, perSeatPrice,
  type QuotaLabels, type ResolvedFeatureRow,
} from './PlanChoice.catalog';

const MONTHS_PER_YEAR = 12;

/** The comparison table drops CRM: it is on for every tier, so it separates nothing. */
const COMPARE_SKIP = new Set(['crm']);

/**
 * Builds a locale-aware currency formatter.
 *
 * @param language - The active i18n language.
 * @returns A formatter for BRL amounts.
 */
function useMoney(language: string) {
  return useMemo(
    () => (value: number) => new Intl.NumberFormat(language, {
      style: 'currency', currency: 'BRL',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value),
    [language],
  );
}

/**
 * What one seat of a plan costs each month, on a given cycle.
 *
 * @param plan - The plan.
 * @param cycle - The billing cycle on screen.
 * @returns The monthly per-seat price.
 */
function seatMonthly(plan: PlanWithFrequency, cycle: BillingCycle): number {
  const monthly = plan.monthlyPrice ?? plan.price;
  const yearly = plan.yearlyPrice;
  const perMonth = cycle === 'yearly' && yearly != null
    ? yearly / MONTHS_PER_YEAR
    : monthly;
  return perSeatPrice(perMonth, plan.users, plan.pricePerSeat != null);
}

/**
 * One tier, as a row the buyer can click straight into checkout.
 *
 * @param props - The plan, the cycle, the quota ladder and the click handler.
 * @returns The row.
 */
const PlanRow: React.FC<{
  plan: PlanWithFrequency;
  billingCycle: BillingCycle;
  quotaLabels: QuotaLabels;
  onSelect: (plan: PlanWithFrequency) => void;
}> = ({ plan, billingCycle, quotaLabels, onSelect }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const money = useMoney(i18n.language);
  // The agency tab never asked whether a plan was on sale, so an announced
  // tier rendered as an ordinary buyable row. Same vocabulary as the solo
  // card: the row says which of the two it is and stops selecting.
  const available = isPlanSelectable(plan);
  const status = getSpotsStatus(plan);
  const facts = agencyRowFacts(plan, quotaLabels[plan.key]);
  // The price column is sized by its content, and its content includes the
  // "R$ X/mês no anual · R$ Y/mês no mensal" note. On a phone that note is
  // most of the screen, so the name-and-description column was squeezed to
  // about 40px and set one word per line, with the badge sitting over the
  // title. Below this width the row is one column and reads top to bottom.
  const stacked = useMediaQuery('(max-width: 640px)');

  // Composed from the catalogue, never written out: a sentence repeating
  // "4x mais IA, 100 GB" is a second copy of three published numbers.
  const summary = facts.factor == null
    ? t('planChoice.agency.summary.baseline')
    : t('planChoice.agency.summary.more', {
      factor: facts.factor,
      storage: facts.storageGb == null
        ? ''
        : formatStorage(facts.storageGb, (n) => n.toLocaleString(i18n.language)),
      highlight: facts.highlight == null
        ? ''
        : t(`planChoice.agency.highlight.${facts.highlight}`),
    });

  const monthlyPerSeat = seatMonthly(plan, 'monthly');
  // Straight off the catalogue: the figure is commercial and has already
  // changed once, so no screen states it.
  const supportPerSeat = plan.limits?.seat?.support_seats ?? 0;

  return (
    // UnstyledButton, not Button: this row *is* a layout -- a four-cell grid --
    // and the DS Button carries `h-9`, a fixed 36px that a 90px row cannot fit
    // inside. The rows then spilled over one another.
    <UnstyledButton
      type="button"
      data-testid={`plan-row-${plan.key}`}
      data-available={available ? 'true' : 'false'}
      onClick={() => available && onSelect(plan)}
      style={{
        display: 'grid',
        gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
        gap: stacked ? 'var(--space-1)' : 'var(--space-1) var(--space-4)',
        alignItems: 'baseline',
        width: '100%', textAlign: 'left', cursor: available ? 'pointer' : 'default',
        padding: 'var(--space-3) 0', background: 'none',
        border: 'none', borderBottom: '1px solid var(--border-ultra-subtle)',
      }}
    >
      <Text component="span" style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', minWidth: 0 }}>
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family-alt-2)',
            fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)',
            letterSpacing: '-0.01em', lineHeight: 1.1,
            color: available ? 'var(--text-primary)' : 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: 0,
          }}
        >
          {plan.title}
        </Text>
        {available && plan.recommended ? (
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-micro)', fontWeight: 'var(--font-bold)',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              padding: '2px var(--space-2)', borderRadius: 'var(--radius-full)',
              background: 'var(--brand-beige)', color: 'var(--seed-base)',
            }}
          >
            {t('planChoice.recommended')}
          </Text>
        ) : null}
        {available ? null : (
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-micro)', fontWeight: 'var(--font-bold)',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              padding: '2px var(--space-2)', borderRadius: 'var(--radius-full)',
              background: 'var(--surface-card)', color: 'var(--text-tertiary-soft)',
            }}
          >
            {t(`planChoice.badge.${status === 'coming_soon' ? 'comingSoon' : 'soldOut'}`)}
          </Text>
        )}
      </Text>
      <Text
        component="span"
        style={{
          fontFamily: 'var(--font-family-alt-2)',
          fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)',
          letterSpacing: '-0.02em', lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          justifySelf: stacked ? 'start' : 'end',
        }}
      >
        {money(seatMonthly(plan, billingCycle))}
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family)', fontSize: 'var(--text-tag)',
            color: 'var(--text-secondary)', fontWeight: 'var(--font-regular)',
            letterSpacing: 'normal',
          }}
        >
          {t('planChoice.perSeatMonth')}
        </Text>
      </Text>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-small)', color: 'var(--text-secondary)',
          lineHeight: 1.45, textWrap: 'pretty',
        }}
      >
        {summary}
      </Text>
      <Text
        component="span"
        data-testid={`plan-row-support-${plan.key}`}
        style={{
          fontSize: 'var(--text-tag)',
          // Green, because it is something gained rather than something
          // owed. The monthly-per-seat note it replaces was the only other
          // thing in this cell and is the weaker of the two: a buyer
          // comparing tiers already has the headline price.
          color: supportPerSeat > 0
            ? 'var(--status-success-text)'
            : 'var(--text-tertiary)',
          justifySelf: stacked ? 'start' : 'end',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {supportPerSeat > 0
          ? t('planChoice.agency.supportPerSeat', { count: supportPerSeat })
          : t('planChoice.agency.rowNote', { monthly: money(monthlyPerSeat) })}
      </Text>
    </UnstyledButton>
  );
};

/**
 * The tier-by-tier table, hidden until the buyer asks for it.
 *
 * @param props - The plans and the quota ladder.
 * @returns The comparison table.
 */
const CompareTable: React.FC<{
  plans: PlanWithFrequency[]; quotaLabels: QuotaLabels;
}> = ({ plans, quotaLabels }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  // Three value columns and a column of feature names do not fit a phone at
  // the desktop type size: the names cannot wrap below their longest word,
  // so the third plan was simply cut off at the card's edge.
  const tight = useMediaQuery('(max-width: 640px)');

  const columns = plans.map((plan) => featureMatrix(
    {
      features: enabledFeatures(plan.features),
      limits: plan.limits,
      quotas: quotaLabels[plan.key] ?? {},
    },
    { storage: (gb) => formatStorage(gb, (n) => n.toLocaleString(i18n.language)) },
  ).filter((row) => !COMPARE_SKIP.has(row.id)));

  const cell = (value: ResolvedFeatureRow['value']): string => {
    if (value.kind === 'amount') return value.text;
    if (value.kind === 'multiplier') return t('planChoice.quotaValue.multiplier', { factor: value.factor });
    if (value.kind === 'absent') return '—';
    return '✓';
  };

  // Each value column takes only what its own text needs, and never wraps:
  // sharing the width evenly squeezed "100 GB" and "4x mais" onto two lines
  // and ran "Rubi" into "Diamante" in the header. The feature names take
  // whatever is left, which is the column that can afford to wrap.
  const valueColumn: React.CSSProperties = {
    width: '1%', whiteSpace: 'nowrap', textAlign: 'right',
    paddingLeft: tight ? 'var(--space-2)' : 'var(--space-3)',
  };
  const valueSize = tight ? 'var(--text-micro)' : 'var(--text-tag)';

  const head: React.CSSProperties = {
    fontSize: valueSize, fontWeight: 'var(--font-semibold)',
    color: 'var(--text-secondary)',
    lineHeight: 1.35, padding: 'var(--space-1) 0',
    ...valueColumn,
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      {/* eslint-disable-next-line no-restricted-syntax -- comparison table not suited for DataGrid */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="plan-compare-table">
        <thead>
          <tr>
            <th style={{ ...head, textAlign: 'left', width: 'auto', whiteSpace: 'normal', paddingLeft: 0 }}>
              {t('planChoice.featuresLabel')}
            </th>
            {plans.map((plan) => <th key={plan.key} style={head}>{plan.title}</th>)}
          </tr>
        </thead>
        <tbody>
          {(columns[0] ?? []).map((row, index) => (
            <tr key={row.id}>
              <td
                style={{
                  fontSize: tight ? 'var(--text-tag)' : 'var(--text-small)',
                  fontWeight: 'var(--font-regular)',
                  color: 'var(--text-primary)', lineHeight: 1.35,
                  padding: 'var(--space-1-5) var(--space-2) var(--space-1-5) 0',
                  borderTop: '1px solid var(--border-ultra-subtle)',
                }}
              >
                {t(`planChoice.matrix.${row.id}`)}
              </td>
              {columns.map((column, planIndex) => {
                const value = column[index]?.value;
                const absent = !value || value.kind === 'absent';
                return (
                  <td
                    key={plans[planIndex].key}
                    style={{
                      fontSize: valueSize, fontWeight: 'var(--font-semibold)',
                      lineHeight: 1.35,
                      padding: 'var(--space-1-5) 0',
                      borderTop: '1px solid var(--border-ultra-subtle)',
                      ...valueColumn,
                      color: absent ? 'var(--plan-feature-off)' : 'var(--seed-base)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {value ? cell(value) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PlanRow;
export { PlanRow, CompareTable, seatMonthly, useMoney };
